import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Home, PhoneCall, Shield, Stethoscope, Calculator, GraduationCap, Package, Calendar, MessageCircle, HeartHandshake, Car, Briefcase, Store, User, Gem, CreditCard, HeartPulse } from 'lucide-react'

const Services = () => {
  // WhatsApp message generator based on service title
  const getWhatsAppMessage = (serviceTitle) => {
    const messages = {
      'Housekeeping Services': 'Hello! I am interested in your Housekeeping Services. I need professional housekeeping staff for my property. Please provide more details about availability and pricing.',
      'Watchman & Security Services': 'Hello! I am interested in your Watchman & Security Services. I need watchman services with accommodation. Please share details and availability.',
      'Ward Boys & Girls (Hospital)': 'Hello! I am interested in your Ward Boys & Girls services for hospitals. I need hospital ward staff for patient care. Please provide more information.',
      'Care Takers': 'Hello! I am interested in your Care Takers services. I need professional care takers for home or elderly care. Please provide more details about availability and pricing.',
      'Telecallers': 'Hello! I am interested in your Telecallers services. I need professional telecalling staff for my business. Please provide more details about availability and pricing.',
      'Security Services': 'Hello! I am interested in your Security Services. I need trained security personnel for my facility. Please share details about your services.',
      'Accounting Jobs': 'Hello! I am interested in your Accounting Jobs services. I need accounting professionals for my business. Please share details about available candidates.',
      'Any Degree Jobs': 'Hello! I am interested in your Any Degree Jobs placement services. I am looking for job opportunities. Please provide more information.',
      
      'Drivers': 'Hello! I am interested in your Driver services. I need professional drivers for my vehicles. Please provide information about availability.',
      'Delivery Boys': 'Hello! I am interested in your Delivery Boys services. I need delivery personnel for my business. Please share details about your services.',
      'Event & Function Services': 'Hello! I am interested in your Event & Function Services. I need complete staffing for an upcoming event. Please provide more details.'
    }
    return encodeURIComponent(messages[serviceTitle] || `Hello! I am interested in your ${serviceTitle}. Please provide more information about your services.`)
  }

  const whatsappNumber = '917989308337'

  const videoByTitle = {
    'Housekeeping Services': '/video/cleaning.mp4',
    'Watchman & Security Services': '/video/security.mp4',
    'Ward Boys & Girls (Hospital)': '/video/boys.mp4',
    'Security Services': '/video/securityy.mp4',
    'Accounting Jobs': '/video/accounting.mp4',
    'Any Degree Jobs': encodeURI('/video/any degree.mp4'),
    
    'Drivers': '/video/driving.mp4',
    'Delivery Boys': '/video/delivery.mp4',
    'Event & Function Services': '/video/events.mp4'
  }

  const mainServices = [
    {
      icon: <Home className="w-8 h-8" />,
      title: 'Housekeeping Services',
      description: 'Professional housekeeping services for both men and women. We provide trained and reliable housekeeping staff for residential and commercial properties.',
      features: ['Men & Women staff available', 'Residential & Commercial', 'Trained professionals', 'Background verified'],
      image: '/service-housekeeping.webp',
      href: '/services/housekeeping'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Watchman & Security Services',
      description: 'Watchman services with accommodation provided. Security personnel also available. For both men and women.',
      features: ['Watchman with accommodation', '24/7 security', 'Trained guards', 'Background verified'],
      image: '/service-security.webp',
      href: '/services/watchman'
    },
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: 'Ward Boys & Girls (Hospital)',
      description: 'Hospital ward boys and girls for patient care and hospital maintenance services.',
      features: ['Men & Women available', 'Hospital trained', 'Patient care', 'Reliable service'],
      image: '/service-wardboys.webp',
      href: '/services/ward-boys'
    },
    {
      icon: <HeartHandshake className="w-8 h-8" />,
      title: 'Care Takers',
      description: 'Trained care takers for elderly care, home assistance, and daily living support. Compassionate staff for families and individuals.',
      features: ['Home & elderly care', 'Men & Women available', 'Verified staff', 'Flexible arrangements'],
      image: '/service-care-takers.avif',
      href: '/services/caretakers'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Security Services',
      description: 'Comprehensive security services with trained security personnel for both men and women.',
      features: ['Men & Women guards', 'Trained professionals', '24/7 coverage', 'Verified staff'],
      image: '/service-security.webp',
      href: '/services/security'
    },
    {
      icon: <PhoneCall className="w-8 h-8" />,
      title: 'Telecallers',
      description: 'Professional telecallers for outbound sales, inbound support, lead generation, and customer engagement.',
      features: ['Trained calling staff', 'Campaign & shift support', 'Office & BPO ready', 'Background verified'],
      image: '/service-telecallers.avif',
      href: '/services/telecallers'
    },
    {
      icon: <Calculator className="w-8 h-8" />,
      title: 'Accounting Jobs',
      description: 'Accounting professionals for various accounting roles. Any degree candidates available.',
      features: ['Any degree accepted', 'Accounting expertise', 'Quick placement', 'Verified credentials'],
      image: '/service-accounting.webp',
      href: '/services/accounting-jobs'
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Any Degree Jobs',
      description: 'Job placement services for candidates with any degree qualification across various sectors.',
      features: ['All degree types', 'Multiple sectors', 'Career guidance', 'Placement support'],
      image: '/service-anydegree.webp',
      href: '/services/any-degree-jobs'
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: 'Drivers',
      description: 'Professional drivers for various vehicle types including personal drivers and commercial drivers.',
      features: ['Licensed drivers', 'All vehicle types', 'Background verified', 'Reliable service'],
      image: '/service-drivers.webp',
      href: '/services/drivers'
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Delivery Boys',
      description: 'Delivery personnel for various delivery services including courier and food delivery.',
      features: ['Quick delivery', 'Reliable service', 'Multiple locations', 'Flexible schedules'],
      image: '/service-delivery.webp',
      href: '/services/delivery-boys'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Event & Function Services',
      description: 'Complete staffing solutions for events, functions, marriage functions, and all types of functions.',
      features: ['Event staffing', 'Marriage functions', 'All function types', 'Complete staffing'],
      image: '/service-events.webp',
      href: '/services/event-services'
    }
  ]

  React.useEffect(() => {
    document.title = "Workforce Solutions & Recruitment Services - Shrijaa Outsourcing Consultants";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Explore our end-to-end manpower solutions: housekeeping, security guards, drivers, accountants, telecallers, ward boys, caretakers, and permanent staffing in Hyderabad.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/services');

    const schemaId = 'services-jsonld-schema';
    let script = document.getElementById(schemaId);
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    const servicesSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Workforce & Staffing Solutions",
      "provider": {
        "@type": "Organization",
        "name": "Shrijaa Outsourcing Consultants",
        "url": "https://shrijaaoutsourcing.com"
      },
      "areaServed": "IN",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Manpower Staffing Catalog",
        "itemListElement": mainServices.map((service, idx) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": service.title,
            "description": service.description
          }
        }))
      }
    };
    
    script.textContent = JSON.stringify(servicesSchema);
    
    return () => {
      const activeScript = document.getElementById(schemaId);
      if (activeScript) {
        activeScript.remove();
      }
    };
  }, []);



  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Our Services</h1>
            <p className="text-lg md:text-xl text-primary-100">
              Comprehensive manpower and staffing solutions for all your business and personal needs
            </p>
            <div className="w-20 h-1 bg-accent-500 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">
              We provide skilled and reliable manpower for various sectors and services
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="md:flex">
                  <div className="md:w-1/2">
                    {videoByTitle[service.title] ? (
                      <video
                        src={videoByTitle[service.title]}
                        className="w-full h-48 md:h-full object-cover bg-black"
                        muted
                        autoPlay
                        loop
                        playsInline
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-48 md:h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="md:w-1/2 p-6">
                    <div className="flex items-center mb-4">
                      <div className="text-primary-600 mr-3">
                        {service.icon}
                      </div>
                      <h3 className="text-xl font-semibold text-secondary-900">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-secondary-600 mb-4">
                      {service.description}
                    </p>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-secondary-600">
                          <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={`https://wa.me/${whatsappNumber}?text=${getWhatsAppMessage(service.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp Us
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Services Redirect Banner */}
      <section className="section-padding bg-gradient-to-r from-primary-800 to-teal-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(20,184,166,0.1),transparent)]"></div>
        <div className="container-max relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-accent-400 font-bold uppercase tracking-wider text-sm px-3 py-1 bg-white/10 rounded-full backdrop-blur-sm">New Offerings</span>
            <h2 className="text-3xl md:text-5xl font-black mt-6 mb-6">
              Looking for Premium Financial Services?
            </h2>
            <p className="text-xl text-primary-100 mb-8 font-light leading-relaxed">
              We now offer customized loan schemes (Business, MSME, Home, Auto, Personal, Gold, Education), Credit Cards, and comprehensive Insurance solutions.
            </p>
            <Link
              to="/financial-services"
              className="inline-flex items-center gap-2 bg-white text-primary-800 hover:bg-primary-50 font-bold py-3 px-8 rounded-xl transition-all duration-200 shadow-lg text-lg"
            >
              Explore Financial Services &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">
              How we ensure the perfect match every time
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Requirement Understanding',
                description: 'We discuss your workforce needs, timelines, and specific requirements in detail.'
              },
              {
                step: '02',
                title: 'Talent Sourcing',
                description: 'Our team sources qualified candidates from our verified database and networks.'
              },
              {
                step: '03',
                title: 'Screening & Shortlisting',
                description: 'Rigorous screening and background verification to shortlist the best candidates.'
              },
              {
                step: '04',
                title: 'Client Interview Coordination',
                description: 'We coordinate interviews and facilitate seamless communication between you and candidates.'
              },
              {
                step: '05',
                title: 'Deployment & Onboarding',
                description: 'Selected candidates are deployed with proper documentation and onboarding support.'
              },
              {
                step: '06',
                title: 'Continuous Support',
                description: 'Ongoing support with replacement guarantees and regular performance follow-ups.'
              }
            ].map((process, index) => (
              <motion.div
                key={process.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">
                  {process.title}
                </h3>
                <p className="text-secondary-600">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="container-max text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Contact us today to find the perfect manpower solution for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-primary-600 hover:bg-primary-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Contact Us Today
            </Link>
            <Link
              to="/about"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Services
